import React, { useEffect, useState } from 'react';
import PageHeading from '../../components/PageHeading';
import { BankOutlined, InboxOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Row, Col, Select, 
	 Button,  Checkbox, message, Flex,
	 Tag, Typography, Upload} from 'antd';
import axiosInstance from '../../utils/axios';

const { Dragger } = Upload;

const AddNewTheatre = () => {
	const { id } = useParams();
	const isUpdate = Boolean(id);
	const [form] = Form.useForm();
	const [states, setStates] = useState([]);
	const [districts, setDistricts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedState, setSelectedState] = useState(null);
	const navigate = useNavigate();
	const [screens, setScreens] = useState([]);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [file, setFile] = useState(null);

	useEffect(() => {
		fetchStates();
	}, []);

	useEffect(() => {
			if (selectedState !== null)
				fetchDistricts();
	}, [selectedState]);

	useEffect(() => {
		if (isUpdate) {
			fetchDetail();
		} else {
			form.resetFields();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	const fetchStates = async () => {
		try {
				const response = await axiosInstance.get("core/state/");
				setStates(response.data);
		} catch (error) {
				message.error("Failed to fetch states");
		}
  }

	const fetchDistricts = async () => {
		try {
			const response = await axiosInstance.get(`core/district/?state_id=${selectedState}`);
			setDistricts(response.data);
		} catch (error) {
			message.error("Failed to fetch districts");
		}
  };

	const handleStateChange = (value) => {
		setSelectedState(value);
		form.setFieldsValue({ district: null });
	};

	const MAX_FILE_SIZE_MB = 1;
	
	const uploadProps = {
		name: "logo",
		multiple: false,

		beforeUpload: (newFile) => {
			const isSupportedImage = [
			"image/jpeg",
			"image/png",
			"image/webp",
			"image/avif",
			].includes(newFile.type);

			if (!isSupportedImage) {
			message.error("Only JPG, PNG, WEBP, or AVIF images are allowed!");
			return Upload.LIST_IGNORE;
			}

			const isLt1MB = newFile.size / 1024 / 1024 < MAX_FILE_SIZE_MB;
			if (!isLt1MB) {
			message.error("Image must be smaller than 1MB!");
			return Upload.LIST_IGNORE;
			}

			setFile({
			uid: newFile.uid,
			name: newFile.name,
			status: "done",
			originFileObj: newFile,
			});

			return false; // prevent auto upload
		},

		onRemove: () => {
			setFile(null);
		},

		fileList: file ? [file] : [],
	};

  
	const createFormData = (data) => {
		const formData = new FormData();
		formData.append("name", data.name);
		formData.append("owner", data.owner);
		formData.append("phone_number", data.phone_number);
		formData.append("email", data.email);
		formData.append("state", data.state);
		formData.append("district", data.district);
		formData.append("address", data.address);
		formData.append("postal_code", data.postal_code);
		formData.append("website", data.website);
		formData.append("total_screens", data.total_screens);
		formData.append("description", data.description);
		formData.append("facilities", data.facilities);
		formData.append("is_active", data.is_active);

		if (file && file.originFileObj) {
      		formData.append("logo", file.originFileObj);
    	}

		return formData;
	};


	const handleFinish = async (values) => {
		setLoading(true);
		const formData = createFormData(values);
		try {
			if (isUpdate) {
				await axiosInstance.put(`theatre-management/theatre/${id}/`, formData);
				navigate(`/admin/theatre`);
			} else {
				const { data } = await axiosInstance.post(`theatre-management/theatre/`, formData);
				message.success("Theatre created successfully");
				form.resetFields();
				navigate(`/admin/theatre/${data.id}`);
			}
		} catch (err) {
			console.log(err);
			message.error("Cannot create theatre");
		} finally {
			setLoading(false);
		}
	}

	const fetchDetail = async () => {
		setLoading(true);
	
		try {
			const { data } = await axiosInstance.get(`theatre-management/theatre/${id}/`);
			
			form.setFieldsValue({
				name: data.name,
				owner: data.owner,
				phone_number: data.phone_number,
				email: data.email,
				state: data.state,
				district: data.district,
				address: data.address,
				postal_code: data.postal_code,
				website: data.website,
				total_screens: data.total_screens,
				description: data.description,
				facilities: data.facilities,
				is_active: data.is_active
			});
			setSelectedState(data.state);
			setScreens(data.screens);
			setFile({
				uid: "-1",
				name: "logo",
				status: "done",
				url: data.logo,
				originFileObj: null, // IMPORTANT
			});


		} catch (err) {
			console.log(err);
			message.error("Cannot fetch theatre detail");
		} finally {
			setLoading(false);
		}
	}

	const deleteTheatre = async () => {
    try {
      await axiosInstance.delete(`theatre-management/theatre/${id}/`);
      navigate('/admin/theatre');
    } catch (error) {
      message.error("Cannot delete");
    } finally {
			setLoading(false);
		}
  }

  return (
		<div className='dashboard'>
			<PageHeading name="Add Or Edit Theatre" icon={<BankOutlined/>} />
			<Row gutter={[16, 16]}>
				<Col xs={24} sm={24} md={24} lg={12}>
					<Form layout="vertical" form={form} name="form" onFinish={handleFinish}>
						<Row  gutter={[16, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Theatre Name"
									name="name"
									rules={[{ required: true, message: 'Please enter name' }]}
								>
									<Input />
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Owner"
									name="owner"
									rules={[{ required: true, message: 'Please enter name' }]}
								>
									<Input />
								</Form.Item>
							</Col>
						</Row>

						<Row  gutter={[16, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Phone Number"
									name="phone_number"
								>
									<Input />
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Email "
									name="email"
								>
									<Input type='email' />
								</Form.Item>
							</Col>
						</Row>

						<Row  gutter={[16, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="State"
									name="state"
								>
									<Select
										allowClear
										style={{ width: '100%' }}
										placeholder="Please select"
										onChange={handleStateChange}
									>
										{
											states.map((item, index) => <Select.Option key={item.id} value={item.id}>{item.name}</Select.Option>)
										}
									</Select>
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="District"
									name="district"
								>
									<Select>
										{
											districts.map((item, index) => <Select.Option key={item.id} value={item.id}>{item.name} - {item.alias}</Select.Option>)
										}
									</Select>
								</Form.Item>
							</Col>
						</Row>

						<Row  gutter={[16, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Address"
									name="address"
								>
									<Input.TextArea rows={1}/>
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Postal Code"
									name="postal_code"
								>
									<Input/>
								</Form.Item>
							</Col>
						</Row>

						<Row  gutter={[16, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Website"
									name="website"
								>
									<Input placeholder='eg: https://www.theatre1.com'/>
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Number of Screens"
									name="total_screens"
									initialValue={1}
								>
									<Input type='number' min={1} max={25}/>
								</Form.Item>
							</Col>
						</Row>

						<Row  gutter={[16, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Description"
									name="description"
								>
									<Input.TextArea rows={4}/>
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Facilities"
									name="facilities"
								>
									<Input.TextArea rows={4} placeholder='Comma separated Eg: 3D, IMAX, Food Court'/>
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Upload Logo"
									required
								>
									<Dragger {...uploadProps}>
										<p className="ant-upload-drag-icon">
										<InboxOutlined />
										</p>
										<p className="ant-upload-text">Click or drag image to upload</p>
										<p className="ant-upload-hint">Only JPG, PNG, WEBP, AVIF — Max 1MB</p>
									</Dragger>
								</Form.Item>
							</Col>
						</Row>

						<Typography.Text type="danger">Cannot set show if deactivated</Typography.Text>&nbsp;
						<Form.Item name="is_active" valuePropName="checked">
							<Checkbox>Is Active</Checkbox>
						</Form.Item>

						<Form.Item>
							<Button type="primary" htmlType="submit" loading={loading}>
								Submit
							</Button>
						</Form.Item>
					</Form>
				</Col>

				<Col xs={24} sm={24} md={24} lg={12}>
					{isUpdate && (
						<div style={{ border: "1px solid #eee", padding: "20px", borderRadius: "6px" }}>
							<h4 style={{ marginBottom: "16px" }}>Screens</h4>
							<Typography.Text type="danger">Reducing Number of screens remove the last screen</Typography.Text>
							{screens && screens.map((item, index) => (
								<div
									key={item.id}
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										padding: "8px 12px",
										border: "1px solid #ddd",
										borderRadius: "4px",
										marginBottom: "8px",
										backgroundColor: "#fafafa"
									}}
								>
									<div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
										<p style={{ margin: 0, fontWeight: 500 }}>{item.name}</p>
										<Tag color={item.is_active ? "green" : "red"}>
											{item.is_active ? "Active" : "Not Active"}
										</Tag>
									</div>
									<Button size="small" type="primary" onClick={() => navigate(`/admin/theatre/screen/${id}/${item.id}`)}>
										Configure
									</Button>
								</div>
							))}
						</div>
					)}
					<br/>
					{isUpdate ? <div className='border-box box-danger'>
						<Typography.Title level={5} color='error' style={{marginTop: "0px"}}>Danger zone</Typography.Title>
						<Typography.Paragraph>
							Do not delete if this theatre is assigned to show or booking is undergoing
						</Typography.Paragraph>
						<Row>
							<Col>
								<Checkbox onChange={(e) => setConfirmDelete(!confirmDelete)}>Confirm to Delete</Checkbox>
							</Col>
							<Col>
								<Button color="danger" variant="solid" onClick={() => deleteTheatre()} disabled={!confirmDelete}>
									Delete
								</Button>
							</Col>
						</Row>
					</div> : null}
				</Col>

			</Row>
    </div>
  )
}

export default AddNewTheatre 